import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ThemeMode } from '@finance/contracts';

const STORAGE_KEY = 'finance:theme';
const PREF_EVENT = 'finance:preferences:changed';

interface ThemeModeContextValue {
  themeMode: ThemeMode;
  resolvedMode: 'light' | 'dark';
  setThemeMode: (mode: ThemeMode) => void;
}

const ThemeModeContext = createContext<ThemeModeContextValue>({
  themeMode: 'system',
  resolvedMode: 'light',
  setThemeMode: () => {},
});

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark' || value === 'system';
}

function readStoredTheme(): ThemeMode | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return isThemeMode(raw) ? raw : null;
  } catch {
    return null;
  }
}

function getSystemTheme(): 'light' | 'dark' {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function ThemeModeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>(() => readStoredTheme() ?? 'system');
  const [systemTheme, setSystemTheme] = useState<'light' | 'dark'>(getSystemTheme);
  const resolvedMode = themeMode === 'system' ? systemTheme : themeMode;

  const setThemeMode = useCallback((mode: ThemeMode) => {
    setThemeModeState(mode);
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, mode);
      } catch {
        // Storage may be unavailable; the in-memory preference remains valid.
      }
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const updateSystemTheme = () => setSystemTheme(mediaQuery.matches ? 'dark' : 'light');

    updateSystemTheme();
    mediaQuery.addEventListener('change', updateSystemTheme);
    return () => mediaQuery.removeEventListener('change', updateSystemTheme);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const syncThemeAcrossTabs = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && isThemeMode(event.newValue)) {
        setThemeModeState(event.newValue);
      }
    };

    window.addEventListener('storage', syncThemeAcrossTabs);
    return () => window.removeEventListener('storage', syncThemeAcrossTabs);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const listener = (event: Event) => {
      const detail = (event as CustomEvent<Partial<{ theme?: ThemeMode }>>).detail;
      if (isThemeMode(detail?.theme)) setThemeMode(detail.theme);
    };

    window.addEventListener(PREF_EVENT, listener);
    return () => window.removeEventListener(PREF_EVENT, listener);
  }, [setThemeMode]);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.theme = resolvedMode;
    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  return (
    <ThemeModeContext.Provider value={{ themeMode, resolvedMode, setThemeMode }}>
      {children}
    </ThemeModeContext.Provider>
  );
}

export function useThemeMode() {
  return useContext(ThemeModeContext);
}
